<script setup lang="ts">
/**
 * 「未开通 · 联系开通」升级位弹窗(library-search-split / t1-feature-extraction)。
 *
 * "可见即诱饵":以图搜图 / 加入搜图库 等 image_search 门控功能**照常露出**,
 * 没买的租户点了 → 弹这个讲价值(不讲价格)。图片搜索文字/标签 mode 不受影响。
 *
 * 用于:图片库做特征 / 图片搜索以图 tab / 详情 modal 单图做特征。
 *
 * 「联系开通」走**人工客服**(加客服微信)—— 功能开通是人工的事;智能客服(iris)
 * 只答程序使用问题、不负责开通,故这里不再唤起智能客服抽屉。
 */
import { ref, watch } from 'vue'
import SupportQrCard from './SupportQrCard.vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    message?: string
    /** 左侧次按钮文案;不传则只有「联系开通」。 */
    backLabel?: string
  }>(),
  {
    title: '以图搜图 · 未开通',
    message:
      '开通后,拍张照(或拖一张图)就能在你的图库里找到同款/相似产品 —— 你和小程序顾客都能用。图库、标签不受影响,照常用。',
    backLabel: ''
  }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'back'): void
  // 保留供父组件 @contact 绑定通过类型检查;现已内切客服联系卡,不再 emit。
  (e: 'contact'): void
}>()

// 「联系开通」内切到客服微信联系卡(人工开通),而非关弹窗 / 开智能客服。
const showContact = ref(false)
// 弹窗关掉后复位,下次打开回到价值介绍页。
watch(
  () => props.open,
  (v) => {
    if (!v) showContact.value = false
  }
)
</script>

<template>
  <div v-if="open" class="mask" @click.self="emit('close')">
    <div class="modal">
      <!-- 价值介绍页 -->
      <template v-if="!showContact">
        <div class="ic"><i class="ti ti-camera-search" aria-hidden="true"></i></div>
        <h4>{{ title }}</h4>
        <p v-if="message">{{ message }}</p>
        <div class="btns">
          <button v-if="backLabel" type="button" class="btn ghost" @click="emit('back')">
            <i class="ti ti-arrow-left" aria-hidden="true"></i> {{ backLabel }}
          </button>
          <button type="button" class="btn primary" @click="showContact = true">
            <i class="ti ti-sparkles" aria-hidden="true"></i> 联系开通
          </button>
        </div>
      </template>

      <!-- 客服微信联系卡(人工开通)-->
      <template v-else>
        <div class="ic wechat"><i class="ti ti-brand-wechat" aria-hidden="true"></i></div>
        <h4>联系客服开通</h4>
        <p>功能开通请加客服微信,我们帮你开通配置。</p>
        <SupportQrCard class="upm-qr" :size="160" />
        <div class="btns">
          <button type="button" class="btn ghost" @click="showContact = false">
            <i class="ti ti-arrow-left" aria-hidden="true"></i> 返回
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.mask {
  position: fixed;
  inset: 0;
  background: rgba(20, 20, 18, 0.32);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 120;
  padding: 24px;
}
.modal {
  width: 360px;
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  padding: 22px;
  text-align: center;
}
.ic {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--color-background-info);
  color: var(--color-text-info);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin: 2px auto 12px;
}
.ic.wechat {
  background: var(--color-background-success);
  color: var(--color-text-success);
}
.modal h4 {
  font-size: var(--fs-page);
  font-weight: var(--fw-bold);
  margin: 0 0 7px;
}
.modal p {
  font-size: var(--fs-item);
  color: var(--color-text-secondary);
  margin: 0 0 18px;
  line-height: 1.6;
}
.upm-qr {
  margin-bottom: 18px;
}
.btns {
  display: flex;
  gap: 9px;
  justify-content: center;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-item);
  font-weight: var(--fw-med);
  padding: 8px 14px;
  border-radius: var(--border-radius-md);
  border: var(--border-width-control) solid var(--color-border-control);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-family: inherit;
}
.btn .ti {
  font-size: var(--fs-page);
}
.btn:hover {
  background: var(--color-background-secondary);
}
.btn.ghost {
  border-color: transparent;
  background: transparent;
  color: var(--color-text-secondary);
}
.btn.primary {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}
.btn.primary:hover {
  filter: brightness(1.06);
}
</style>
